import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { ChoiceChip } from '@components';
import {
  CandidateRegion,
  IStatusFacet,
  isSenioritySelected,
  toggle,
  toggleSeniority,
} from '@utils';
import { SeniorityOrder } from '@constants';

export interface ICandidateFilterSectionsProps {
  regions: CandidateRegion[];
  seniorities: string[];
  statusFacets: IStatusFacet[];
  selectedRegions: CandidateRegion[];
  selectedSeniorities: string[];
  selectedStatuses: string[];
  onChange: (patch: {
    regions?: CandidateRegion[];
    seniorities?: string[];
    statuses?: string[];
  }) => void;
}

export const offersChoice = (optionCount: number) => optionCount > 1;

export const FilterSectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    variant="caption"
    component="h3"
    color="text.secondary"
    sx={{
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0.32px',
      textTransform: 'uppercase',
    }}
  >
    {children}
  </Typography>
);

export const FilterSection: React.FC<{
  title: string;
  showsSingleOption?: boolean;
  children: React.ReactNode;
}> = ({ title, showsSingleOption = false, children }) =>
  (
    showsSingleOption
      ? React.Children.count(children) > 0
      : offersChoice(React.Children.count(children))
  ) ? (
    <Stack gap={1}>
      <FilterSectionTitle>{title}</FilterSectionTitle>
      <Box display="flex" flexWrap="wrap" gap={0.75}>
        {children}
      </Box>
    </Stack>
  ) : null;

export const offersCandidateFilterChoice = ({
  statusFacets,
  regions,
  seniorities,
}: Pick<
  ICandidateFilterSectionsProps,
  'statusFacets' | 'regions' | 'seniorities'
>) =>
  [statusFacets, regions, seniorities].some(({ length }) =>
    offersChoice(length),
  );

export const RegionFilterSection: React.FC<{
  regions: CandidateRegion[];
  selected: CandidateRegion[];
  onChange: (regions: CandidateRegion[]) => void;
  showsSingleOption?: boolean;
}> = ({ regions, selected, onChange, showsSingleOption }) => (
  <FilterSection title="Region" showsSingleOption={showsSingleOption}>
    {regions.map((region) => (
      <ChoiceChip
        key={region}
        selected={selected.includes(region)}
        onClick={() => onChange(toggle(selected, region))}
      >
        {region}
      </ChoiceChip>
    ))}
  </FilterSection>
);

export const SeniorityFilterSection: React.FC<{
  seniorities: string[];
  selected: string[];
  onChange: (seniorities: string[]) => void;
  showsSingleOption?: boolean;
}> = ({ seniorities, selected, onChange, showsSingleOption }) => (
  <FilterSection title="Seniority" showsSingleOption={showsSingleOption}>
    {[...seniorities]
      .sort((a, b) => SeniorityOrder.indexOf(a) - SeniorityOrder.indexOf(b))
      .map((seniority) => (
        <ChoiceChip
          key={seniority}
          selected={isSenioritySelected(selected, seniority)}
          onClick={() =>
            onChange(toggleSeniority(selected, seniority, seniorities))
          }
        >
          {seniority}
        </ChoiceChip>
      ))}
  </FilterSection>
);

export const CandidateFilterSections: React.FC<
  ICandidateFilterSectionsProps
> = ({
  regions,
  seniorities,
  statusFacets,
  selectedRegions,
  selectedSeniorities,
  selectedStatuses,
  onChange,
}) => (
  <>
    <FilterSection title="By stage">
      {statusFacets.map((facet) => (
        <ChoiceChip
          key={facet.key}
          selected={selectedStatuses.includes(facet.key)}
          onClick={() =>
            onChange({ statuses: toggle(selectedStatuses, facet.key) })
          }
        >
          {facet.label}
        </ChoiceChip>
      ))}
    </FilterSection>

    <RegionFilterSection
      regions={regions}
      selected={selectedRegions}
      onChange={(next) => onChange({ regions: next })}
    />

    <SeniorityFilterSection
      seniorities={seniorities}
      selected={selectedSeniorities}
      onChange={(next) => onChange({ seniorities: next })}
    />
  </>
);
